/**
 * 用户本地设置的**文件存储**（桌面端）—— localStorage 只当"会话内缓存"。
 *
 * ## 为什么需要它（用户建议 + 实测教训）
 *
 * 这些设置原本只存在 WebView2 的 `localStorage` 里，而 localStorage 按**来源**
 * （`scheme://host:port`）隔离 —— 本地服务端口一变，用户所有设置就等于被清空。
 *
 * 实测踩过：生产端本地端口原本"每次启动随机"，WebView2 数据目录里因此出现过 **12 个不同来源**，
 * 表现为"每次重启设置都重置"（老板键、侧栏/面板宽度、界面缩放、暗色模式全丢），
 * 以及"进创作页窗口被拉长"（前端读不到宽度记录，每次都按首次启动重设窗口）。
 *
 * 端口现在会复用（`config::local_port`），但仍有"被别的程序占用 → 退回系统分配"的情况，
 * 所以设置必须落在**与端口无关的文件**里：`%APPDATA%\Xiaotianshu\settings.json`
 * （服务端见 `apps/desktop/src-tauri/src/settings.rs`）。
 *
 * ## 做法（刻意选"最不需要改调用点"的一种）
 *
 * 1. **启动时灌入**：`hydrateSettings()` 把文件里的键值写进 localStorage —— 在 React 渲染**之前**
 *    完成，于是所有同步读取 localStorage 的既有代码（面板宽度、老板键、缩放…）都不用改；
 * 2. **写入时镜像**：`installSettingsMirror()` 给 `localStorage.setItem/removeItem` 加一层，
 *    变更按 key 合并、防抖后 POST 回文件。这样连将来新增的设置项也自动被持久化，
 *    不需要每处都记得调用一个 helper。
 *
 * 网页端不做任何事（没有本地服务，也没有"端口变了导致来源变化"的问题）。
 */

import { isDesktopHost } from './host';

const ENDPOINT = '/__xts/settings';
/** 合并写入的防抖（拖动改变宽度时会连着写很多次） */
const FLUSH_DEBOUNCE_MS = 400;

let mirrorInstalled = false;

/**
 * 启动时把文件里的设置灌进 localStorage。
 *
 * ⚠️ 必须在**首次渲染之前** await（见 `web-entry.tsx`）：`loadLayout()` 这类同步读取
 * 发生在首个渲染期间，晚一步就会拿到空值，表现仍是"设置丢了"。
 *
 * 文件是**权威来源**：同名的 localStorage 项会被文件里的值覆盖。
 * 拿不到文件（网页端 / 服务未就绪）时静默跳过，行为退回旧版（纯 localStorage）。
 */
export async function hydrateSettings(): Promise<void> {
  if (!isDesktopHost()) return;
  try {
    const res = await fetch(ENDPOINT, { credentials: 'include' });
    if (!res.ok) return;
    const data = (await res.json()) as Record<string, unknown>;
    const originalSet = localStorage.setItem.bind(localStorage);
    for (const [key, value] of Object.entries(data ?? {})) {
      if (typeof value === 'string') originalSet(key, value);
    }
    installSettingsMirror();
  } catch {
    /* 服务还没起来 / 网络异常：继续用 localStorage（旧行为，不阻塞启动） */
  }
}

/** 给 localStorage 的写入加一层"镜像回文件"（幂等） */
export function installSettingsMirror(): void {
  if (mirrorInstalled) return;
  mirrorInstalled = true;

  const pending = new Map<string, string | null>();
  let timer: number | undefined;

  const flush = () => {
    timer = undefined;
    if (pending.size === 0) return;
    const body = JSON.stringify(Object.fromEntries(pending));
    pending.clear();
    void fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch(() => {
      /* 写失败不打扰用户：localStorage 里仍然是对的，下一次改动会再带上新值重试 */
    });
  };

  const schedule = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(flush, FLUSH_DEBOUNCE_MS);
  };

  const originalSet = localStorage.setItem.bind(localStorage);
  const originalRemove = localStorage.removeItem.bind(localStorage);

  // 只镜像本应用的键（`xiaotianshu:*` / `dark-mode` / `ui-font-scale`），
  // 免得把宿主或其它东西塞进来的键也写进我们的文件
  const isOurs = (key: string): boolean =>
    key.startsWith('xiaotianshu:') || key.startsWith('ui-font-scale') || key === 'dark-mode';

  try {
    localStorage.setItem = function patchedSetItem(key: string, value: string) {
      originalSet(key, value);
      if (isOurs(key)) {
        pending.set(key, String(value));
        schedule();
      }
    } as typeof localStorage.setItem;

    localStorage.removeItem = function patchedRemoveItem(key: string) {
      originalRemove(key);
      if (isOurs(key)) {
        pending.set(key, null);
        schedule();
      }
    } as typeof localStorage.removeItem;
  } catch {
    // 极端情况下（宿主不允许改写）退回"只灌入、不镜像"：至少读得到设置
    mirrorInstalled = false;
  }

  // 页面关闭前把没来得及发出去的改动补一次（keepalive 让请求能在卸载中完成）
  window.addEventListener('pagehide', () => {
    if (pending.size === 0) return;
    const body = JSON.stringify(Object.fromEntries(pending));
    pending.clear();
    try {
      void fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    } catch {
      /* 忽略：这只是尽力而为 */
    }
  });
}
